const definitions = [
  {
    key: "governance.regulatory",
    name: "Regulatory & Governance Intelligence",
    preferredAgent: "regulatory-governance-agent",
    prompt: "Explain the verified regulatory updates, applicability results, formula links, evidence gaps and open actions on this page. Distinguish draft, adopted and effective requirements and cite the official source shown.",
    matches: (path) => path.startsWith("/vessel-performance") && /(?:\?|&)tab=governance(?:&|$)/.test(path),
  },
  {
    key: "vessel-performance.analysis",
    name: "Vessel Performance",
    preferredAgent: "vessel-performance-analyst",
    prompt: "Review the performance evidence on this page. Separate facts, deterministic calculations and hypotheses; identify material exceptions, missing inputs and the next human checks.",
    matches: (path) => path.startsWith("/vessel-performance"),
  },
  {
    key: "environment.compliance",
    name: "Environment & Carbon Compliance",
    preferredAgent: "cii-environmental-agent",
    prompt: "Explain the environmental and carbon-compliance evidence on this page, including the applicable ruleset, missing inputs, calculation basis and items requiring human verification.",
    matches: (path) => /environment|emission|marpol|imo-dcs|fueleu|eu-ets|cii|eeoi/.test(path),
  },
  {
    key: "reporting.noon-report",
    name: "Noon Reports",
    preferredAgent: "noon-report-quality-agent",
    prompt: "Check this noon-report page for completeness, time-distance-speed consistency, fuel and ROB reconciliation, unit problems and evidence that needs human correction.",
    matches: (path) => /noon-report|vessel-reports|daily-report|engine-log/.test(path),
  },
  {
    key: "reporting.engine",
    name: "Engine Reports",
    preferredAgent: "engine-intelligence-agent",
    prompt: "Review the engine evidence visible here. Highlight abnormal load, power, RPM, fuel, SFOC, temperature, pressure or running-hour relationships and recommend human checks.",
    matches: (path) => /main-engine|auxiliary-engine|engine-register|ocon|crankshaft|stern-tube|machinery/.test(path),
  },
  {
    key: "navigation.voyage",
    name: "Voyage, Route & Navigation",
    preferredAgent: "port-voyage-agent",
    prompt: "Review the voyage and navigation evidence on this page for route, position, ETA, distance, speed, weather and milestone inconsistencies. Provide review points, not navigation commands.",
    matches: (path) => /voyage|route|port-arrival|port-departure|sosp|eosp|compass|gmdss|deck-log|night-order/.test(path),
  },
  {
    key: "maintenance.work-orders",
    name: "Work Orders",
    preferredAgent: "engine-intelligence-agent",
    prompt: "Review the work-order evidence on this page and prepare a prioritized, evidence-linked follow-up for human approval.",
    matches: (path) => /work-orders|work-order/.test(path),
  },
  {
    key: "maintenance.defects",
    name: "Defects & Findings",
    preferredAgent: "engine-intelligence-agent",
    prompt: "Review the defect and finding evidence visible here, identify operational risk and dependencies, and recommend accountable next checks without changing a record.",
    matches: (path) => /defect|finding/.test(path),
  },
  {
    key: "maintenance.vessel-pms",
    name: "Vessel PMS",
    preferredAgent: "engine-intelligence-agent",
    prompt: "Review this vessel PMS page for overdue or upcoming maintenance, counter evidence, prerequisites, spares and conflicts. Return a draft for human review.",
    matches: (path) => /vessel-pms|maintenance|pms/.test(path),
  },
  {
    key: "drydock.projects",
    name: "Dry Dock",
    preferredAgent: "sms-compliance-agent",
    prompt: "Review this dry-dock page for scope, safety, class, material, dependency and evidence gaps. Produce a review checklist only.",
    matches: (path) => /drydock|dry-dock/.test(path),
  },
  {
    key: "procurement.requisitions",
    name: "Procurement & Requisitions",
    preferredAgent: "fuel-rob-agent",
    prompt: "Review the procurement evidence visible here for urgency, vessel need, stock, specification and approval dependencies. Do not place or approve an order.",
    matches: (path) => /procurement|requisition|inventory|purchase|delivery-note/.test(path),
  },
  {
    key: "hseq.incidents",
    name: "HSEQ & Incidents",
    preferredAgent: "sms-compliance-agent",
    prompt: "Review this HSEQ evidence, trace each concern to the visible record, and prepare an accountable corrective-action checklist for human review.",
    matches: (path) => /safety|near-miss|incident|inspection|vetting|psc|permit/.test(path),
  },
  {
    key: "certificate.required-actions",
    name: "Certificates & Required Actions",
    preferredAgent: "sms-compliance-agent",
    prompt: "Review certificate and required-action evidence visible here, prioritizing expiry, verification, owner and due-date gaps without certifying compliance.",
    matches: (path) => /certificate/.test(path),
  },
  {
    key: "crew.management",
    name: "Crew Management",
    preferredAgent: "fleet-superintendent-agent",
    prompt: "Summarize the crew-management evidence on this page, highlight readiness or data gaps, and identify follow-up for the authorized shore or vessel role.",
    matches: (path) => /crew|user|designation|role|manning/.test(path),
  },
  {
    key: "documents.operations",
    name: "Documents, Forms & Templates",
    preferredAgent: "sms-compliance-agent",
    prompt: "Review the document, form or template evidence on this page for completeness, version control and accountable follow-up. Do not publish or sign anything.",
    matches: (path) => /document|form-builder|report-builder|template|my-forms/.test(path),
  },
  {
    key: "master-data.import-mapping",
    name: "Import & Mapping",
    preferredAgent: "fleet-superintendent-agent",
    prompt: "Review this import or mapping page for identity, schema, unit, duplicate and evidence-lineage risks before a human commits changes.",
    matches: (path) => /import-mapping/.test(path),
  },
  {
    key: "master-data.job-list",
    name: "PMS Job List",
    preferredAgent: "engine-intelligence-agent",
    prompt: "Review the PMS job catalogue visible here for duplicates, missing maker evidence, unclear intervals, ranks, safety controls and spare requirements.",
    matches: (path) => /job-list|component-linked-jobs/.test(path),
  },
  {
    key: "settings.master-data",
    name: "Master Data",
    preferredAgent: "fleet-superintendent-agent",
    prompt: "Explain the master-data evidence on this page and identify completeness, duplicate, ownership and downstream consistency risks.",
    matches: (path) => /master-data|ship-type|vessel-group|activity-type|unlocode|impa/.test(path),
  },
  {
    key: "settings.administration",
    name: "Administration & Settings",
    preferredAgent: "fleet-superintendent-agent",
    prompt: "Explain the configuration and governance visible on this page, including permission or integration risks. Never reveal secrets or suggest bypassing access controls.",
    matches: (path) => /settings|access-control|rbac|system-config|sso/.test(path),
  },
  {
    key: "vessel.management",
    name: "Vessel Management",
    preferredAgent: "fleet-superintendent-agent",
    prompt: "Summarize the vessel particulars and operational evidence on this page, highlighting missing or inconsistent records and accountable follow-up.",
    matches: (path) => /vessel|fleet-map/.test(path),
  },
  {
    key: "dashboard.fleet-overview",
    name: "Fleet Overview",
    preferredAgent: "fleet-summary-analyst",
    prompt: "Summarize the fleet evidence visible on this page by vessel, highlighting material exceptions, stale data and the next superintendent checks.",
    matches: (path) => path === "/" || /dashboard/.test(path),
  },
];

const fallback = {
  key: "operations.general",
  name: "Vessel Operations",
  preferredAgent: "fleet-superintendent-agent",
  prompt: "Review the operational evidence visible on this page. Separate facts, calculations and recommendations; identify missing evidence and accountable human follow-up.",
};

export const resolveAgentPage = (pathname = "/", search = "") => {
  const normalized = `${String(pathname || "/").toLowerCase()}${String(search || "").toLowerCase()}`;
  return definitions.find((definition) => definition.matches(normalized)) || fallback;
};

export const safeQueryContext = (search = "") => {
  const blocked = /token|secret|password|authorization|credential|api[-_]?key|pin/i;
  const values = {};
  new URLSearchParams(search).forEach((value, key) => {
    if (!blocked.test(key)) values[key] = String(value).slice(0, 500);
  });
  return values;
};

export default definitions;
